import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArchiveRestore, Boxes, Edit3, Eye, PackagePlus, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FilterPanel, SortableHeader } from "../../components/data-controls";
import {
  Badge,
  Button,
  Confirm,
  EmptyState,
  ErrorState,
  IconButton,
  Input,
  Loading,
  PageHeader,
  Pagination,
  SearchBox,
  Select,
} from "../../components/ui";
import { adminApi, assetUrl } from "../../lib/api";
import { dateTime, money, number } from "../../lib/format";
import { labelOf } from "../../lib/labels";
import { PERMISSIONS, hasPermission } from "../../lib/permissions";
import { useAuthStore } from "../../store/auth-store";
import type { AdminProductListItem, ProductGender, ProductStatus } from "../../types/api";

type StockState = "" | "in" | "low" | "out";

export function ProductListPage() {
  const user = useAuthStore((state) => state.user);
  const canManage = hasPermission(user, PERMISSIONS.productsManage);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<ProductStatus | "">("");
  const [brandId, setBrandId] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [gender, setGender] = useState<ProductGender | "">("");
  const [stockState, setStockState] = useState<StockState>("");
  const [featured, setFeatured] = useState<"" | "true" | "false">("");
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [sort, setSort] = useState("updated-desc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [target, setTarget] = useState<AdminProductListItem | null>(null);
  const queryClient = useQueryClient();
  const brands = useQuery({ queryKey: ["brands"], queryFn: adminApi.brands.list });
  const categories = useQuery({ queryKey: ["categories"], queryFn: adminApi.categories.list });
  const query = useQuery({
    queryKey: ["products", search, status, brandId, categoryId, gender, stockState, featured, minPrice, maxPrice, sort, page, pageSize],
    queryFn: () => adminApi.products.list({
      search,
      status,
      brandId,
      categoryId,
      gender,
      stockState,
      featured: featured === "" ? "" : featured === "true",
      minPrice: minPrice ? Number(minPrice) : undefined,
      maxPrice: maxPrice ? Number(maxPrice) : undefined,
      sort,
      page,
      pageSize,
    }),
  });

  const remove = useMutation({
    mutationFn: (id: string) => adminApi.products.remove(id),
    onSuccess: async () => {
      setTarget(null);
      await queryClient.invalidateQueries({ queryKey: ["products"] });
    },
  });

  const resetPage = () => setPage(1);
  const activeFilters = useMemo(
    () => [search, status, brandId, categoryId, gender, stockState, featured, minPrice, maxPrice].filter(Boolean).length,
    [search, status, brandId, categoryId, gender, stockState, featured, minPrice, maxPrice],
  );

  const clearFilters = () => {
    setSearch("");
    setStatus("");
    setBrandId("");
    setCategoryId("");
    setGender("");
    setStockState("");
    setFeatured("");
    setMinPrice("");
    setMaxPrice("");
    setSort("updated-desc");
    setPage(1);
  };

  const changeSort = (value: string) => {
    setSort(value);
    setPage(1);
  };

  return (
    <>
      <PageHeader
        title="مدیریت محصولات"
        description="محصولات، قیمت، موجودی و وضعیت انتشار را سریع بررسی کنید؛ فیلترهای پیشرفته فقط در صورت نیاز باز می‌شوند."
        actions={canManage ? (
          <>
            <Link className="button button--ghost" to="/products/trash"><Trash2 size={17} /> زباله‌دان</Link>
            <Link className="button button--primary" to="/products/new"><PackagePlus size={18} /> محصول جدید</Link>
          </>
        ) : undefined}
      />

      <FilterPanel
        search={<SearchBox value={search} onChange={(value) => { setSearch(value); resetPage(); }} placeholder="نام، اسلاگ، برند یا SKU..." />}
        activeCount={activeFilters}
        totalLabel={`${number(query.data?.totalCount ?? 0)} محصول`}
        onClear={clearFilters}
      >
        <Select label="وضعیت" value={status} onChange={(event) => { setStatus(event.target.value as ProductStatus | ""); resetPage(); }}>
          <option value="">همه وضعیت‌ها</option>
          <option value="Published">منتشرشده</option>
          <option value="Draft">پیش‌نویس</option>
          <option value="Archived">بایگانی</option>
        </Select>
        <Select label="برند" value={brandId} onChange={(event) => { setBrandId(event.target.value); resetPage(); }}>
          <option value="">همه برندها</option>
          {brands.data?.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
        </Select>
        <Select label="دسته‌بندی" value={categoryId} onChange={(event) => { setCategoryId(event.target.value); resetPage(); }}>
          <option value="">همه دسته‌ها</option>
          {categories.data?.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
        </Select>
        <Select label="جنسیت" value={gender} onChange={(event) => { setGender(event.target.value as ProductGender | ""); resetPage(); }}>
          <option value="">همه جنسیت‌ها</option>
          <option value="Women">زنانه</option>
          <option value="Men">مردانه</option>
          <option value="Unisex">یونیسکس</option>
        </Select>
        <Select label="موجودی" value={stockState} onChange={(event) => { setStockState(event.target.value as StockState); resetPage(); }}>
          <option value="">همه موجودی‌ها</option>
          <option value="in">موجود</option>
          <option value="low">رو به اتمام</option>
          <option value="out">ناموجود</option>
        </Select>
        <Select label="محصول ویژه" value={featured} onChange={(event) => { setFeatured(event.target.value as "" | "true" | "false"); resetPage(); }}>
          <option value="">همه محصولات</option>
          <option value="true">فقط ویژه</option>
          <option value="false">غیر ویژه</option>
        </Select>
        <Input label="حداقل قیمت" type="number" min="0" inputMode="numeric" placeholder="مثلاً 500000" value={minPrice} onChange={(event) => { setMinPrice(event.target.value); resetPage(); }} />
        <Input label="حداکثر قیمت" type="number" min="0" inputMode="numeric" placeholder="مثلاً 5000000" value={maxPrice} onChange={(event) => { setMaxPrice(event.target.value); resetPage(); }} />
        <Select label="مرتب‌سازی" value={sort} onChange={(event) => changeSort(event.target.value)}>
          <option value="updated-desc">آخرین ویرایش</option>
          <option value="created-desc">جدیدترین ثبت</option>
          <option value="name-asc">نام: الف تا ی</option>
          <option value="name-desc">نام: ی تا الف</option>
          <option value="price-asc">کمترین قیمت</option>
          <option value="price-desc">بیشترین قیمت</option>
          <option value="stock-asc">کمترین موجودی</option>
          <option value="stock-desc">بیشترین موجودی</option>
        </Select>
      </FilterPanel>

      {query.isLoading ? (
        <Loading />
      ) : query.error ? (
        <ErrorState error={query.error} retry={() => void query.refetch()} />
      ) : !query.data?.items.length ? (
        <EmptyState title="محصولی با این فیلترها پیدا نشد" description="فیلترها را تغییر دهید یا محصول جدیدی ثبت کنید." />
      ) : (
        <div className="data-table-block">
          <div className="table-card product-table-card">
          <div className="table-scroll">
            <table className="product-table">
              <thead>
                <tr>
                  <SortableHeader label="محصول" value={sort} ascValue="name-asc" descValue="name-desc" onChange={changeSort} />
                  <SortableHeader label="قیمت" value={sort} ascValue="price-asc" descValue="price-desc" onChange={changeSort} />
                  <SortableHeader label="موجودی" value={sort} ascValue="stock-asc" descValue="stock-desc" onChange={changeSort} />
                  <th>وضعیت</th>
                  <SortableHeader label="آخرین تغییر" value={sort} descValue="updated-desc" defaultDirection="desc" onChange={changeSort} />
                  <th>عملیات</th>
                </tr>
              </thead>
              <tbody>
                {query.data.items.map((row) => (
                  <tr key={row.product.id}>
                    <td data-label="محصول">
                      <div className="product-cell">
                        <div className="product-thumb">{row.product.image ? <img src={assetUrl(row.product.image)} alt={row.product.name} /> : <Boxes />}</div>
                        <div>
                          <strong>{row.product.name}</strong>
                          <span>{row.product.brand} · {row.product.concentration}{row.product.volume ? ` · ${row.product.volume}` : ""}</span>
                          <small dir="ltr">/{row.product.slug}</small>
                        </div>
                      </div>
                    </td>
                    <td data-label="قیمت">
                      <div className="cell-stack">
                        <strong>{money(row.product.price)}</strong>
                        {row.product.originalPrice ? <small className="muted strike">{money(row.product.originalPrice)}</small> : null}
                      </div>
                    </td>
                    <td data-label="موجودی">
                      <span className={row.product.stock <= 0 ? "danger-text" : row.product.stock <= 5 ? "warning-text" : "success-text"}>
                        {row.product.stock <= 0 ? "ناموجود" : `${number(row.product.stock)} عدد`}
                      </span>
                    </td>
                    <td data-label="وضعیت">
                      <div className="cell-stack">
                        <Badge value={labelOf(row.status)} tone={row.status === "Published" ? "success" : row.status === "Archived" ? "danger" : "neutral"} />
                        {row.isFeatured ? <Badge value="ویژه" tone="warning" /> : null}
                      </div>
                    </td>
                    <td data-label="آخرین تغییر">
                      <div className="cell-stack">
                        <span>{dateTime(row.updatedAt || row.createdAt)}</span>
                        <small>ثبت: {dateTime(row.createdAt)}</small>
                      </div>
                    </td>
                    <td data-label="عملیات">
                      <div className="table-actions">
                        <Link className="icon-button" title="جزئیات محصول" aria-label="جزئیات محصول" to={`/products/${row.product.id}/details`}><Eye /></Link>
                        {canManage ? <Link className="icon-button" title="ویرایش محصول" aria-label="ویرایش محصول" to={`/products/${row.product.id}`}><Edit3 /></Link> : null}
                        {canManage ? <IconButton label="انتقال به زباله‌دان" onClick={() => setTarget(row)}><Trash2 /></IconButton> : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </div>
          <Pagination
            page={query.data.page}
            totalPages={query.data.totalPages}
            totalCount={query.data.totalCount}
            pageSize={pageSize}
            onChange={setPage}
            onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
          />
        </div>
      )}

      <Confirm
        open={Boolean(target)}
        title="انتقال محصول به زباله‌دان"
        description={`«${target?.product.name ?? ""}» از فروشگاه پنهان و به زباله‌دان منتقل می‌شود. بعداً می‌توانید آن را بازیابی یا برای همیشه حذف کنید.`}
        confirmLabel="انتقال به زباله‌دان"
        busy={remove.isPending}
        onClose={() => setTarget(null)}
        onConfirm={() => target && remove.mutate(target.product.id)}
      />
    </>
  );
}

export function ProductTrashPage() {
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("deleted-desc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [permanent, setPermanent] = useState<AdminProductListItem | null>(null);
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ["products-trash", search, sort, page, pageSize],
    queryFn: () => adminApi.products.trash({ search, sort, page, pageSize }),
  });
  const restore = useMutation({
    mutationFn: adminApi.products.restore,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["products-trash"] });
      await queryClient.invalidateQueries({ queryKey: ["products"] });
    },
  });
  const remove = useMutation({
    mutationFn: adminApi.products.removePermanent,
    onSuccess: async () => {
      setPermanent(null);
      await queryClient.invalidateQueries({ queryKey: ["products-trash"] });
    },
  });
  const changeSort = (value: string) => { setSort(value); setPage(1); };

  return (
    <>
      <PageHeader
        title="زباله‌دان محصولات"
        description="محصولات حذف‌شده را بازیابی کنید یا پس از اطمینان، برای همیشه حذف کنید."
        actions={<Link className="button button--ghost" to="/products"><ArchiveRestore size={17} /> بازگشت به محصولات</Link>}
      />

      <FilterPanel
        search={<SearchBox value={search} onChange={(value) => { setSearch(value); setPage(1); }} placeholder="جست‌وجو در زباله‌دان..." />}
        activeCount={search ? 1 : 0}
        totalLabel={`${number(query.data?.totalCount ?? 0)} محصول حذف‌شده`}
        onClear={() => { setSearch(""); setSort("deleted-desc"); setPage(1); }}
      >
        <Select label="مرتب‌سازی" value={sort} onChange={(event) => changeSort(event.target.value)}>
          <option value="deleted-desc">آخرین حذف</option>
          <option value="name-asc">نام: الف تا ی</option>
          <option value="name-desc">نام: ی تا الف</option>
        </Select>
      </FilterPanel>

      {query.isLoading ? (
        <Loading />
      ) : query.error ? (
        <ErrorState error={query.error} retry={() => void query.refetch()} />
      ) : !query.data?.items.length ? (
        <EmptyState title="زباله‌دان خالی است" description="محصولی برای بازیابی یا حذف دائم وجود ندارد." />
      ) : (
        <div className="data-table-block">
          <div className="table-card">
          <div className="table-scroll">
            <table className="product-table management-table trash-table">
              <thead>
                <tr>
                  <SortableHeader label="محصول" value={sort} ascValue="name-asc" descValue="name-desc" onChange={changeSort} />
                  <th>برند</th>
                  <th>تنوع‌ها</th>
                  <SortableHeader label="آخرین تغییر" value={sort} descValue="deleted-desc" defaultDirection="desc" onChange={changeSort} />
                  <th>عملیات</th>
                </tr>
              </thead>
              <tbody>
                {query.data.items.map((row) => (
                  <tr key={row.product.id}>
                    <td data-label="محصول"><div className="cell-stack"><strong>{row.product.name}</strong><small dir="ltr">/{row.product.slug}</small></div></td>
                    <td data-label="برند">{row.product.brand}</td>
                    <td data-label="تنوع‌ها">{number(row.variantCount)}</td>
                    <td data-label="آخرین تغییر">{dateTime(row.updatedAt || row.createdAt)}</td>
                    <td data-label="عملیات">
                      <div className="table-actions">
                        <Button variant="secondary" busy={restore.isPending} onClick={() => restore.mutate(row.product.id)}><ArchiveRestore size={16} /> بازیابی</Button>
                        <Button variant="danger" onClick={() => setPermanent(row)}><Trash2 size={16} /> حذف دائم</Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </div>
          <Pagination
            page={query.data.page}
            totalPages={query.data.totalPages}
            totalCount={query.data.totalCount}
            pageSize={pageSize}
            onChange={setPage}
            onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
          />
        </div>
      )}

      <Confirm
        open={Boolean(permanent)}
        title="حذف دائمی محصول"
        description={`«${permanent?.product.name ?? ""}» به‌صورت دائمی از پایگاه داده حذف می‌شود و قابل بازیابی نیست. این عملیات فقط برای مواردی انجام شود که واقعاً دیگر لازم نیستند.`}
        confirmLabel="حذف برای همیشه"
        busy={remove.isPending}
        onClose={() => setPermanent(null)}
        onConfirm={() => permanent && remove.mutate(permanent.product.id)}
      />
    </>
  );
}
